import { validationConstants } from "constants/formValidation";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Controller, useFormContext } from "react-hook-form";
import { IAccount } from "types/Account/Account";
import { IDirectPayLinkPublicConfig } from "types/DirectPayLink.d";

interface IVisitInfoProps {
  linkConfig?: IDirectPayLinkPublicConfig | null;
  isInternalUser: boolean;
  account?: IAccount | null;
}

export function VisitInfo({
  linkConfig,
  isInternalUser,
  account,
}: IVisitInfoProps) {
  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext();
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();

  // Hide account number field if link config says so
  const showAccountNumber = linkConfig ? linkConfig.showAccountNumber : true;

  // For public links, account number is optional
  // For internal use, account number is required
  const isAccountNumberOptional = !isInternalUser;

  // With a custom or main link: show location based on link config (showAccountLocation).
  // Without a link: show location based on account setting (locationEnabled.directPay).
  // Locations: only public links get accountLocations in link config; main link and no-link use account.accountLocations.
  const locationEnabledByLink = !!linkConfig?.showAccountLocation;
  const locationEnabledByAccount = !!account?.locationEnabled?.directPay;
  const locationOptions = account
    ? account.accountLocations ?? []
    : linkConfig?.accountLocations ?? [];
  const showLocationDropdown =
    (linkConfig ? locationEnabledByLink : locationEnabledByAccount) &&
    locationOptions.length > 0;

  if (!showAccountNumber && !showLocationDropdown) {
    return null;
  }

  return (
    <>
      {showAccountNumber && (
        <div className="field flex flex-column col-12 md:col-6">
          <Label htmlFor="accountNumber">
            Account Number{" "}
            {isAccountNumberOptional && (
              <span className="text-gray-500 text-sm">(Optional)</span>
            )}
          </Label>

          <Controller
            name="accountNumber"
            control={control}
            rules={
              !isAccountNumberOptional
                ? { required: validationConstants.VISIT_INFO_FORM.VISIT_ID }
                : undefined
            }
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <InputText
                  data-testid="accountNumber"
                  id="accountNumber"
                  placeholder="Account Number"
                  autoComplete="off"
                  value={value || ""}
                  onBlur={() =>
                    handleOnBlurField({
                      onBlur,
                      field: "accountNumber",
                      trigger,
                    })
                  }
                  onChange={(e) => onChange(e.target.value)}
                />

                {getFormErrorMessage("accountNumber", errors)}
              </>
            )}
          />
        </div>
      )}
      {showLocationDropdown && (
        <div className="field flex flex-column col-12 md:col-6">
          <Label htmlFor="accountLocationId">Location</Label>
          <Controller
            data-testid="accountLocationId"
            name="accountLocationId"
            control={control}
            rules={{
              required: validationConstants.VISIT_INFO_FORM.LOCATION,
            }}
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <Dropdown
                  id="accountLocationId"
                  data-testid="accountLocationId"
                  value={value ?? ""}
                  onChange={(e) => {
                    onChange(e.value ?? null);
                    trigger("accountLocationId");
                  }}
                  options={locationOptions}
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Select location"
                  className="w-full"
                />
                {fieldState.error?.message && (
                  <small className="p-error">{fieldState.error.message}</small>
                )}
              </>
            )}
          />
        </div>
      )}
    </>
  );
}
